import type React from "react";
import { useState, useEffect, useMemo } from "react";
import CommonModal from "@/components/common/CommonModal";
import SearchSelect from "@/components/common/SearchSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PlusCircle, Coins, Trash2, CheckCircle2 } from "lucide-react";
import type { ISalaryPreviewUser } from "@/interface/attendance.interface";
import type {
  ISalaryHead,
  IBranchWiseSalaryHead,
} from "@/interface/salaryHead.interface";
import { showToastNotification } from "@/utility/helper";

export type SalaryHeadType = "earning" | "deduction";

interface AddSalaryHeadModalProps {
  show: boolean;
  onClose: () => void;
  selectedUser: ISalaryPreviewUser | null;
  branchSalaryHeads?: IBranchWiseSalaryHead[];
  onAddSalaryHead: (
    userId: string,
    salaryHeadId: string,
    amount: number,
    headType: SalaryHeadType,
    headName?: string,
  ) => void;
  onRemoveSalaryHead?: (userId: string, salaryHeadId: string) => void;
}

interface SelectOptionHead {
  id: string;
  name: string;
  type: SalaryHeadType;
  currentAmount?: number;
}

export const AddSalaryHeadModal: React.FC<AddSalaryHeadModalProps> = ({
  show,
  onClose,
  selectedUser,
  branchSalaryHeads = [],
  onAddSalaryHead,
  onRemoveSalaryHead,
}) => {
  const [headType, setHeadType] = useState<SalaryHeadType>("earning");
  const [selectedHeadId, setSelectedHeadId] = useState<string>("");
  const [amount, setAmount] = useState<string>("");

  // Extract and combine available salary heads from selectedUser and branchSalaryHeads
  const availableHeads = useMemo<SelectOptionHead[]>(() => {
    const list: SelectOptionHead[] = [];
    const idSet = new Set<string>();

    // 1. First add heads from selectedUser.userSalary
    selectedUser?.userSalary?.forEach((h: ISalaryHead) => {
      const hId = h.branchSalaryHeadId || h.id;
      const hName = h.salaryName || h.name;
      const rawType = (h.type || "").toLowerCase();
      const type: SalaryHeadType =
        rawType === "deduction" ? "deduction" : "earning";

      if (hId && !idSet.has(hId)) {
        idSet.add(hId);
        list.push({
          id: hId,
          name: hName || "Salary Head",
          type,
          currentAmount: Number(h.amount) || 0,
        });
      }
    });

    // 2. Also add from branchSalaryHeads if not already present
    branchSalaryHeads.forEach((bh: IBranchWiseSalaryHead) => {
      const bhId = bh.id;
      const bhName = bh.salaryName || bh.salaryHead?.name;
      const rawType = (bh.salaryHead?.type || "").toLowerCase();
      const type: SalaryHeadType =
        rawType === "deduction" ? "deduction" : "earning";

      if (bhId && !idSet.has(bhId)) {
        idSet.add(bhId);
        list.push({
          id: bhId,
          name: bhName || "Branch Head",
          type,
          currentAmount: Number(bh.amount) || 0,
        });
      }
    });

    return list;
  }, [selectedUser, branchSalaryHeads]);

  // Filter salary heads by selected headType (Earning vs Deduction)
  const filteredHeads = useMemo(() => {
    return availableHeads.filter((h) => h.type === headType);
  }, [availableHeads, headType]);

  // Options for SearchSelect
  const headOptions = useMemo(() => {
    return filteredHeads.map((head) => ({
      value: head.id,
      label: `${head.name}${head.currentAmount ? ` (Current: ₹${head.currentAmount})` : ""
        }`,
    }));
  }, [filteredHeads]);

  // Reset or prefill form when modal opens or headType/selectedUser changes
  useEffect(() => {
    if (show) {
      if (filteredHeads.length > 0) {
        const firstHead = filteredHeads[0];
        setSelectedHeadId(firstHead.id);
        setAmount(firstHead.currentAmount ? String(firstHead.currentAmount) : "");
      } else {
        setSelectedHeadId("");
        setAmount("");
      }
    }
  }, [show, headType, filteredHeads]);

  // Update amount input when selected salary head changes
  const handleHeadSelectChange = (headId: string) => {
    setSelectedHeadId(headId);
    const target = filteredHeads.find((h) => h.id === headId);
    if (target && target.currentAmount !== undefined) {
      setAmount(String(target.currentAmount));
    } else {
      setAmount("");
    }
  };

  // Handle Form Submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedUser?.user?.id) {
      showToastNotification("danger", "No user selected");
      return;
    }

    if (!selectedHeadId) {
      showToastNotification("danger", "Please select a salary head");
      return;
    }

    const numericAmount = Number(amount);
    if (isNaN(numericAmount) || numericAmount < 0) {
      showToastNotification("danger", "Please enter a valid non-negative amount");
      return;
    }

    const selectedHead = availableHeads.find((h) => h.id === selectedHeadId);
    const headName = selectedHead?.name;

    onAddSalaryHead(
      selectedUser.user.id,
      selectedHeadId,
      numericAmount,
      headType,
      headName,
    );

    onClose();
  };

  // Get list of custom added heads for this employee
  const customAddedHeads = useMemo(() => {
    return (selectedUser?.userSalary || []).filter(
      (h) => h.isCustomAdded === true && Number(h.amount) > 0,
    );
  }, [selectedUser]);

  return (
    <CommonModal
      show={show}
      onClose={onClose}
      title=""
      className="sm:max-w-lg rounded-2xl p-0  shadow-2xl border border-slate-200"
    >
      <div className="p-6 space-y-5 bg-white">
        {/* Header Icon & Title */}
        <div className="flex flex-col items-center text-center">
          <div className="h-12 w-12 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mb-2.5 border border-blue-100 shadow-xs">
            <Coins size={24} className="text-blue-600" />
          </div>
          <h3 className="text-lg font-bold text-slate-900 font-heading">
            Add / Update Salary Head
          </h3>
          <p className="text-xs text-slate-500 mt-1 leading-relaxed">
            Manage custom earnings or deductions for{" "}
            <span className="font-semibold text-slate-800">
              {selectedUser?.user?.name || "Employee"}
            </span>
          </p>
        </div>

        {/* User Badge Info Card */}
        <div className="bg-gradient-to-br from-slate-50 to-blue-50/40 p-3.5 rounded-xl border border-slate-200/80 flex items-center justify-between text-xs shadow-xs">
          <div>
            <span className="text-slate-500 font-medium">Employee Code: </span>
            <span className="font-bold text-slate-800">
              {selectedUser?.user?.employeeCode || "N/A"}
            </span>
          </div>
          <div>
            <span className="text-slate-500 font-medium">Net Salary: </span>
            <span className="font-extrabold text-blue-700 bg-blue-100/70 px-2 py-0.5 rounded-md border border-blue-200">
              ₹{selectedUser?.totals?.netSalary ?? 0}
            </span>
          </div>
        </div>

        {/* Currently Added Custom Heads List (if any) */}
        {customAddedHeads.length > 0 && (
          <div className="bg-slate-50 p-3 rounded-xl border border-slate-200/80 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
                <CheckCircle2 size={13} className="text-emerald-600" />
                Custom Added Heads ({customAddedHeads.length})
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto pr-1">
              {customAddedHeads.map((h) => {
                const headId = h.branchSalaryHeadId || h.id;
                const isEarn = (h.type || "").toLowerCase() === "earning";
                const badgeStyle = isEarn
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-rose-50 text-rose-700 border-rose-200";

                return (
                  <span
                    key={headId}
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg border ${badgeStyle}`}
                  >
                    <span>
                      {h.salaryName || h.name}: ₹{h.amount}
                    </span>
                    {onRemoveSalaryHead && selectedUser?.user?.id && (
                      <button
                        type="button"
                        onClick={() =>
                          onRemoveSalaryHead(selectedUser.user.id, headId)
                        }
                        className="text-slate-400 hover:text-rose-600 transition-colors p-0.5 rounded hover:bg-white/80 cursor-pointer"
                        title="Remove head"
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Form Inputs */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* 1. Type Select (Earning vs Deduction) */}
          <div className="space-y-1.5">
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
              Salary Type <span className="text-rose-500">*</span>
            </label>
            <Select
              value={headType}
              onValueChange={(val: SalaryHeadType) => {
                setHeadType(val);
              }}
            >
              <SelectTrigger className="w-full h-10 text-xs sm:text-sm font-medium border-slate-200 focus:ring-2 focus:ring-primary/20 bg-white rounded-xl shadow-xs flex justify-between items-center px-3 text-slate-800 cursor-pointer hover:border-slate-300 transition-colors">
                <SelectValue placeholder="Select salary type" />
              </SelectTrigger>
              <SelectContent className="bg-white border border-slate-200 shadow-xl rounded-xl p-1 z-[9999]">
                <SelectItem
                  value="earning"
                  className="text-xs sm:text-sm font-semibold py-2 px-3 text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors cursor-pointer"
                >
                  Earning
                </SelectItem>
                <SelectItem
                  value="deduction"
                  className="text-xs sm:text-sm font-semibold py-2 px-3 text-rose-700 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                >
                  Deduction
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* 2. Salary Head SearchSelect */}
          <div className="space-y-1.5">
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
              Salary Head <span className="text-rose-500">*</span>
            </label>
            {filteredHeads.length > 0 ? (
              <SearchSelect
                placeholder="Search & select salary head..."
                options={headOptions}
                value={selectedHeadId}
                onChange={handleHeadSelectChange}
                className="w-full text-xs sm:text-sm font-medium"
              />
            ) : (
              <div className="p-3 rounded-xl bg-amber-50 text-amber-800 border border-amber-200 text-xs font-medium">
                No {headType} heads available for this employee.
              </div>
            )}
          </div>

          {/* 3. Amount Input */}
          <div className="space-y-1.5">
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
              Amount (₹) <span className="text-rose-500">*</span>
            </label>
            <Input
              type="number"
              min="0"
              step="any"
              placeholder="Enter amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="h-10 text-xs sm:text-sm font-medium border-slate-200 focus:ring-2 focus:ring-primary/20 bg-white rounded-xl shadow-xs"
              required
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3 w-full pt-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 h-10 text-xs sm:text-sm font-semibold border-slate-200 text-slate-700 hover:bg-slate-100 rounded-xl transition-all cursor-pointer"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 h-10 text-xs sm:text-sm font-bold bg-primary hover:bg-primary/90 text-white rounded-xl shadow-xs transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <PlusCircle size={16} />
              Save Salary Head
            </Button>
          </div>
        </form>
      </div>
    </CommonModal>
  );
};

export default AddSalaryHeadModal;
